import { A7Router } from '@ark7/utils';
import {
  AccessLogService,
  TrackingService,
  UserService,
} from '@ark7/resources-common';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  Component,
  HostBinding,
  Inject,
  OnInit,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { ExperimentService, GtagService } from '@ark7/layout';
import { Identity } from '@ark7/identity';
import { NavigationEnd, Router } from '@angular/router';
import { SocketService, UserBehaviorService } from '@ark7/socket';
import { isPlatformBrowser } from '@angular/common';

import { environment } from '../environments/environment';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
  user: Identity;

  @HostBinding('class.ark7') _hostClass = true;

  // not using local storage here, as expired auth may cause the login form hidden
  userAuthorized: Date;

  @HostBinding('class.hide')
  get _hideClass() {
    return !(this.hide === false) || !isPlatformBrowser(this.platformId);
  }

  private hide: boolean;
  private initialized = false;

  constructor(
    _accessLogService: AccessLogService,
    _experiment: ExperimentService,
    _socket: SocketService,
    _userBehavior: UserBehaviorService,
    _trackingService: TrackingService,
    breakpointObserver: BreakpointObserver,
    gtag: GtagService,
    renderer: Renderer2,
    private router: A7Router,
    private router2: Router,
    private userService: UserService,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {
    _socket.connect();

    this.userService.userChange$.subscribe((user) => {
      this.user = user;
      if (user != null) {
        this.userAuthorized = new Date();
        gtag.setUserId(this.user.uuid.toString());
      }
      this.checkAuthentication();
      if (this.hide == null) {
        this.hide = false;
      }
    });

    if (isPlatformBrowser(platformId)) {
      breakpointObserver
        .observe([Breakpoints.HandsetPortrait])
        .subscribe((result) => {
          if (result.matches) {
            renderer.addClass(document.body, 'handset');
          } else {
            renderer.removeClass(document.body, 'handset');
          }
        });
    }
  }

  async ngOnInit() {
    // Reveal content immediately — the prerendered HTML is fully rendered.
    // The hide class exists to prevent a flash before redirecting authenticated
    // users to /dashboard, but checkAuthentication() handles that by setting
    // hide=true and doing window.location.href, so we don't need to block on fetch.
    this.hide = false;

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.initialized = true;
        if (isPlatformBrowser(this.platformId)) {
          window?.scrollTo(0, 0);
        }
        this.checkAuthentication();
      }
    });

    // First time to fetch the user
    if (isPlatformBrowser(this.platformId)) {
      try {
        await this.userService.fetch();
      } catch (e) {
        this.hide = false;
      }
    }

    if (this.userAuthorized == null) {
      this.hide = false;
    }

    this.userService.userChange$.subscribe((user) => {
      if (user != null) {
        this.userAuthorized = new Date();
      }
    });
  }

  private checkAuthentication() {
    if (
      this.initialized &&
      isPlatformBrowser(this.platformId) &&
      (this.router.url === '/' || this.router.url.startsWith('/#') || this.router.url.startsWith('/?')) &&
      this.user != null
    ) {
      this.hide = true;
      const authenticatedRedirectUrl = environment.authenticatedRedirectUrl || (environment as any).environment?.authenticatedRedirectUrl;
      if (this.router.url === authenticatedRedirectUrl) {
        window?.location.reload();
      } else {
        const isAbsoluteUrl = authenticatedRedirectUrl.startsWith('http://') || authenticatedRedirectUrl.startsWith('https://');
        
        if (isAbsoluteUrl) {
          const urlTree = this.router2.parseUrl(this.router.url);
          const queryParams = urlTree.queryParams;
          const queryString = Object.keys(queryParams).length > 0 
            ? '?' + new URLSearchParams(queryParams as any).toString()
            : '';
          window.location.href = authenticatedRedirectUrl + queryString;
        } else {
          const urlTree = this.router2.parseUrl(this.router.url);
          const queryParams = urlTree.queryParams;
          const redirectUrlTree = this.router2.createUrlTree(
            [authenticatedRedirectUrl],
            { queryParams: queryParams },
          );
          window.location.href = redirectUrlTree.toString();
        }
      }
    }
  }
}
